import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Coins } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center">
                <Coins className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-gray-900">BuyMeGold</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-500 mb-8">Last updated: January 2026</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                By accessing and using BuyMeGold ("the Service"), you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-600 leading-relaxed">
                BuyMeGold is an educational tool that allows users to track hypothetical or real gold holdings,
                explore gold's historical performance, and estimate potential future values based on different 
                scenarios. The Service uses public market data to provide illustrative calculations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                To access certain features, you may create an account using your email address or Google account.
                You are responsible for:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Maintaining the confidentiality of your account</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us of any unauthorized use</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Not Financial Advice</h2>
              <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg mb-4">
                <p className="text-yellow-800 font-medium">
                  Important: The information provided by BuyMeGold is for educational and informational purposes only.
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Service does not provide:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Financial advice</li>
                <li>Investment recommendations</li>
                <li>Tax advice</li>
                <li>Legal advice</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                Always consult with qualified financial professionals before making any investment decisions. 
                Past performance does not guarantee future results.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Accuracy</h2>
              <p className="text-gray-600 leading-relaxed">
                We strive to provide accurate data from reputable sources (LBMA, ECB). However:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-4">
                <li>Data may be delayed or indicative</li>
                <li>Prices shown may differ from actual trading prices</li>
                <li>We do not guarantee the accuracy, completeness, or timeliness of any data</li>
                <li>Calculation results are estimates based on simplified models</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed">
                To the maximum extent permitted by law, BuyMeGold and its operators shall not be liable for 
                any direct, indirect, incidental, special, consequential, or punitive damages resulting from 
                your use of the Service or reliance on any information provided.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Contact</h2>
              <p className="text-gray-600 leading-relaxed">
                For questions about these Terms, please contact us at{' '}
                <a href="mailto:legal@buymegold.com" className="text-[#D4AF37] hover:underline">
                  legal@buymegold.com
                </a>
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-500">
            © {new Date().getFullYear()} BuyMeGold. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
